// ABOUTME: Resolves the single voice state every visualizer renders (cavalry cluster, pill, bars).
// ABOUTME: Owns who-is-talking semantics: 'speaking' is the agent, 'listening' is the guest.

/**
 * Collapses the four independent signals App.js tracks into the one string a visualizer takes.
 *
 * Lives here rather than inline in App.js for two reasons: App.js is frozen for new logic, and
 * the rules below are product rules that need a test — they have been got wrong twice now.
 *
 * ─── 2026-09-01: 'idle' split out of 'listening' ────────────────────────────────────────────
 *
 * 'speaking' means somebody — either side — is talking. It is NOT per-speaker: the art team
 * confirmed (2026-09-01) that the speaking animation is the one to play for the guest's voice as
 * well as the agent's. An intermediate version of this file cut the two apart ('speaking' = agent,
 * 'listening' = guest); that was reverted on their feedback. Do not re-split without asking them.
 *
 * What DID change, and the reason this file was touched at all: 'listening' used to double as the
 * resting state whenever nobody was talking. That left the rig's authored idle animation
 * ("Idle - Waiting") completely unused, and the cluster sat in the Listening shape for the whole
 * time the guest was silent — which is most of a session, and is the thing that read as wrong.
 *
 * So the states are now:
 *
 *   'speaking'  — anybody is talking. Audio-reactive to whoever it is.
 *   'thinking'  — the agent is working on a turn.
 *   'idle'      — nobody is talking. The resting state, and the default.
 *   'listening' — NO LONGER RETURNED. Consumers may still handle the string; nothing emits it.
 *
 * 'idle' is a NEW return value. Nothing returned it before, so every consumer needs a branch for
 * it — CavalryViz aliases it onto the `idleWaiting` shape, ParticlePill already had an 'idle'
 * entry in its shape table. A consumer that does not know the string falls through to its own
 * default, which is a look bug rather than a crash.
 *
 * Precedence, highest first:
 *
 *  1. `reactionState` ('cart' | 'wishlist') — a tool call just landed. Deliberately ABOVE the mute
 *     rule: reactions are triggered by taps and agent tool calls, not by the microphone, so a
 *     muted guest still gets their own add-to-cart acknowledged. Muting the mic is not the same
 *     as muting the interface.
 *  2. 'speaking' — anybody talking. Three routes, all covered: the `isAgentSpeaking` flag, the
 *     `isUserSpeaking` flag, and `agentState`, which the transport sets to 'speaking' on its own.
 *     Checking only the flags would miss that back door.
 *  3. `reactionState` 'error' — the thinking watchdog's silence beat (state/thinkingSilenceBeat.js,
 *     2026-09-01): a dead turn flashes "didn't catch that" before the cluster rests. BELOW the
 *     speaking routes on purpose (review 2026-09-01): the beat must never mask a live voice — a
 *     late agent answer, or the guest trying again (the LIKELY next event after a dead turn),
 *     takes the screen instantly. Still above thinking/idle, and like the other reactions it
 *     survives the mute rule (a muted guest's dead typed turn still gets the beat).
 *  4. 'thinking' — from `agentState`.
 *  5. 'idle'.
 *
 * The mute rule (JC, 2026-08-28; narrowed 2026-09-01): with the mic off the cluster must stay on
 * screen and keep working, but the guest's own voice must not drive it. It now gates ONLY the
 * `isUserSpeaking` route, where it used to suppress every route to 'speaking' including the
 * agent's. The old version meant a muted guest watched the cluster sit dead while the agent talked
 * over it, which is wrong on its face — muting your microphone is an input gesture and has nothing
 * to do with the agent's audio, which is still playing and still audible.
 *
 * 'thinking' is intentionally left alone. It reports the agent working on a typed message, which
 * is exactly the state a guest who has just muted their mic is most likely to be in.
 */
export function resolveVisualizerState({
  reactionState = null,
  agentState = 'listening',
  isAgentSpeaking = false,
  isUserSpeaking = false,
  isMicOn = true,
} = {}) {
  if (reactionState && reactionState !== 'error') return reactionState;
  if (isAgentSpeaking || agentState === 'speaking') return 'speaking';
  if (isUserSpeaking && isMicOn) return 'speaking';
  if (reactionState === 'error') return 'error';
  if (agentState === 'thinking') return 'thinking';
  return 'idle';
}

export default resolveVisualizerState;
